/**
 * AI 对话助手 - 调用后台启用的文本/对话模型
 * 流式统一走后端 /api/ai/chat/stream（火山引擎 / 千问 / 钱多多）
 */

import { alibabaAI } from './api';
import { getTextModelConfig, type TextModelConfig } from './storyboard';
import { clearAuthScope, getToken, replaceToken } from '../utils/authStorage';

const DEFAULT_BASE_URL = 'https://ark.cn-beijing.volces.com/api/v3';

const SYSTEM_PROMPT = `你是AI创作平台的对话助手，友好、专业。你可以帮助用户：
- 生成或优化视频提示词、图生视频提示词
- 优化视频脚本、分镜脚本
- 提供视频创作灵感和建议
- 优化文生图（AI绘图）提示词
请用简洁清晰的中文回答，必要时分点或分段落。`;

export type ChatMessage = { role: 'system' | 'user' | 'assistant'; content: string };

/**
 * 获取对话用文本模型配置（与故事板/分镜共用）
 */
export async function getChatModelConfig(): Promise<TextModelConfig | null> {
  return getTextModelConfig();
}

function assertChatConfig(config: TextModelConfig | null) {
  if (!config?.model_name) {
    throw new Error('请先在后台配置并启用文本/对话模型');
  }
  if (config.provider !== 'alibaba' && !config.api_key) {
    throw new Error('请先在后台配置文本模型的 API Key');
  }
}

function buildApiMessages(messages: ChatMessage[]) {
  return [
    { role: 'system' as const, content: SYSTEM_PROMPT },
    ...messages
      .filter((m) => m.role !== 'system')
      .map((m) => ({ role: m.role, content: m.content })),
  ];
}

/** 解析 OpenAI 兼容 SSE，逐块回调文本 */
async function consumeSseResponse(
  response: Response,
  onChunk: (text: string) => void
): Promise<string> {
  const reader = response.body?.getReader();
  if (!reader) throw new Error('无法读取流式响应');

  const decoder = new TextDecoder();
  let buffer = '';
  let fullContent = '';

  const handleDataLine = (trimmed: string) => {
    if (!trimmed || trimmed === 'data: [DONE]') return;
    if (!trimmed.startsWith('data: ')) return;
    try {
      const json = JSON.parse(trimmed.slice(6)) as {
        choices?: Array<{ delta?: { content?: string } }>;
      };
      const content = json.choices?.[0]?.delta?.content;
      if (typeof content === 'string' && content) {
        fullContent += content;
        onChunk(content);
      }
    } catch {
      // 忽略单条解析失败
    }
  };

  try {
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split('\n');
      buffer = lines.pop() ?? '';
      for (const line of lines) {
        handleDataLine(line.trim());
      }
    }
    if (buffer.trim()) {
      handleDataLine(buffer.trim());
    }
  } finally {
    reader.releaseLock();
  }

  return fullContent.trim();
}

/**
 * 调用 Chat Completions，返回助手回复内容（非流式，供其它模块复用）
 */
export async function chatCompletion(
  config: TextModelConfig | null,
  messages: ChatMessage[]
): Promise<string> {
  assertChatConfig(config);

  const apiMessages = buildApiMessages(messages);

  if (config!.provider === 'alibaba') {
    const data = await alibabaAI.chat(apiMessages, { max_tokens: 2048, model_id: config!.id });
    const content = data?.choices?.[0]?.message?.content?.trim();
    if (content == null) throw new Error('模型未返回内容，请重试');
    return content;
  }

  const baseUrl = (config!.base_url || DEFAULT_BASE_URL).replace(/\/$/, '');
  const url = `${baseUrl}/chat/completions`;

  const response = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${config!.api_key}`,
    },
    body: JSON.stringify({
      model: config!.model_name,
      messages: apiMessages,
      max_tokens: 2048,
    }),
  });

  if (!response.ok) {
    const err = await response.json().catch(() => ({})) as { error?: { message?: string } };
    throw new Error(err?.error?.message || `请求失败 ${response.status}`);
  }

  const data = (await response.json()) as {
    choices?: Array<{ message?: { content?: string } }>;
  };
  const content = data.choices?.[0]?.message?.content?.trim();
  if (content == null) throw new Error('模型未返回内容，请重试');
  return content;
}

/**
 * 流式调用：统一走后端 SSE 代理（火山引擎 / 千问 / 钱多多）
 */
export async function chatCompletionStream(
  config: TextModelConfig | null,
  messages: ChatMessage[],
  onChunk: (text: string) => void
): Promise<string> {
  assertChatConfig(config);

  const apiMessages = buildApiMessages(messages);
  const token = getToken();

  const response = await fetch('/api/ai/chat/stream', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify({
      messages: apiMessages,
      max_tokens: 2048,
      model_id: config!.id,
    }),
  });

  // 滑动续期
  const newToken = response.headers.get('X-New-Token');
  if (newToken) replaceToken(newToken);

  if (!response.ok) {
    if (response.status === 401) {
      clearAuthScope();
      if (!window.location.pathname.startsWith('/login')) {
        window.location.href = '/login';
      }
    }
    const err = await response.json().catch(() => ({})) as { error?: string };
    throw new Error(err?.error || `请求失败 ${response.status}`);
  }

  return consumeSseResponse(response, onChunk);
}
